import React, { useCallback, useEffect, useState } from 'react';

import TypeaheadSearchForm from '~/components/Common/Forms/TypeaheadSearchForm';

import SearchService, { TypeaheadSearchResult } from '~/services/SearchService';

const MINIMUM_SEARCH_STRING = 2;

interface Props {
  types: Array<string>;
  label: string;
  required?: boolean;
  initialValue?: string;
  multiple?: boolean;
  onCurrentValueChange: (value: TypeaheadSearchResult | Array<TypeaheadSearchResult> | undefined) => void;
  onItemSelected?: (selection: TypeaheadSearchResult) => void;
}

function PlaceLookUp({ types, label, onCurrentValueChange, required = true, initialValue, multiple = false }: Props) {
  const [value, setValue] = useState<string>(initialValue || '');
  const [open, setOpen] = useState<boolean>(false);
  const [results, setResults] = useState<Array<TypeaheadSearchResult>>([]);
  const [selectedItems, setSelectedItems] = useState<Array<TypeaheadSearchResult>>([]);
  const priority = 'current';

  useEffect(() => {
    if (multiple && initialValue) {
      const initialItems = initialValue.split(',').map((item) => ({
        id: item.trim(),
        format: { mainText: item.trim(), secondaryText: '' },
        searchType: '',
        name: item.trim(),
      }));
      setSelectedItems(initialItems);
    }
  }, [multiple, initialValue]);

  const search = useCallback(async () => {
    if (value.length < MINIMUM_SEARCH_STRING) {
      return;
    }

    if (types.length === 0) {
      return;
    }

    const res = await SearchService.getTypeahead(value, {
      type: types,
      brand: 'luxuryescapes',
      region: 'AU',
      limit: 20,
      priority,
    });

    setResults(res.result);
  }, [value, types, priority]);

  useEffect(() => {
    const t = setTimeout(search, 500);

    return () => {
      clearTimeout(t);
    };
  }, [search]);

  const onSelect = async (selection: TypeaheadSearchResult) => {
    if (multiple) {
      const newSelectedItems = [...selectedItems, selection];
      setSelectedItems(newSelectedItems);
      onCurrentValueChange(newSelectedItems);
    } else {
      onCurrentValueChange(selection);
    }
    setValue('');
  };

  const removeItem = (itemToRemove: TypeaheadSearchResult) => {
    const newSelectedItems = selectedItems.filter((item) => item.id !== itemToRemove.id);
    setSelectedItems(newSelectedItems);
    onCurrentValueChange(newSelectedItems);
  };

  return (
    <TypeaheadSearchForm
      setValue={setValue}
      value={value}
      setOpen={setOpen}
      open={open}
      onSelect={onSelect}
      results={results}
      label={label}
      required={required}
      multiple={multiple}
      selectedItems={selectedItems}
      onRemoveItem={removeItem}
      onCurrentValueChange={onCurrentValueChange}
    />
  );
}

export default PlaceLookUp;
